/**
 * Account slice — Redux Toolkit state for the customer-portal sub-pages
 * (Dashboard, Orders list, Artwork library, Addresses, Payment methods).
 *
 * Scope (SF-ACCT-001 footprint):
 *   - `recentOrders` / `recentOrdersLoading` / `recentOrdersError` —
 *     populated by `fetchRecentOrders`. The Dashboard preview reads up
 *     to 5; the full list page (SF-ACCT-002) will reuse this slice with
 *     a wider limit + pagination.
 *   - `artworkCount` / `artworkLoading` / `artworkError` — populated by
 *     `fetchArtworkCount`. Stores only the `meta.total` from the artwork
 *     endpoint; the Artwork Library page (SF-ACCT-005) will populate the
 *     `files` array on its own selector when it lands.
 *   - `addresses` / `paymentMethods` — placeholder arrays kept here so
 *     the `selectAccountCompletionStatus` selector can read both
 *     "the user has at least one saved address" and "the user has at
 *     least one payment method" without forcing the dashboard to fetch
 *     either list. SF-ACCT-006 / SF-ACCT-007 will populate these via
 *     dedicated thunks.
 *   - `reorderInFlightId` — id of the order whose reorder thunk is
 *     currently dispatching. Used by the dashboard's quick-reorder
 *     button to render its loading state. SF-ACCT-004 owns the full
 *     reorder implementation; this ticket ships the stub.
 *     Note: SF-ACCT-004's planning AC placed this in `cartSlice`, but
 *     reorder state lives here intentionally — the quick-reorder
 *     button and the partial-reorder modal both render inside the
 *     account portal, and the in-flight indicator is read by account
 *     views, not by cart components. Cart sees only the resolved
 *     payload (via `addItem`) after the reorder action resolves.
 *
 * Each loading/error pair is co-located on the slice so a UI section can
 * read them as a single sub-tree without coupling to sibling sections —
 * a failure in `fetchRecentOrders` does not affect `artworkLoading`.
 *
 * SF-ACCT-001.
 */
import { createAsyncThunk, createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import * as accountApi from '@dfq/shared/api/account';
import type {
  AccountArtworkSummary,
  AccountOrderDetail,
  AccountOrderSummary,
  AccountPaginationMeta,
  DeleteAccountArtworkConflict,
  GetAccountArtworkParams,
  GetAccountOrdersParams,
  PaymentMethodDTO,
} from '@dfq/shared/api/account';
import * as cartApi from '@dfq/shared/api/cart';
import { isApiError, type ApiError } from '@dfq/shared/api/_envelope';
import type { AuthState } from '@dfq/shared/stores/authSlice';
import type {
  AccountProfile,
  AccountProfileUpdateInput,
  Address,
  AddressInput,
} from '@dfq/shared/types/account';

// ----------------------------------------------------------------------------
// State shape
// ----------------------------------------------------------------------------

/**
 * Loading status for an individual data section. Each thunk owns its
 * own status so a slow / failed orders fetch never blocks the artwork
 * count from rendering.
 */
export type AccountSectionStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

export interface AccountSectionError {
  message: string;
  /** HTTP status code, when known. 0 for non-axios errors. */
  status?: number;
  /** Backend error code (e.g. `RATE_LIMITED`), when present. */
  code?: string;
}

/**
 * Failure shape for `deleteArtworkFile`. Extends the section error with
 * the deleted file's id (so the view can clear the alert when the row
 * is later re-deleted or refreshed) and the optional list of order
 * numbers returned by a 409 conflict.
 */
export interface AccountArtworkDeleteError extends AccountSectionError {
  fileId: number;
  /** Order numbers returned by the platform on a 409 conflict. */
  referencedOrderNumbers?: string[];
}

/**
 * Saved address record displayed in the customer-portal address book.
 *
 * SF-ACCT-007 promoted the previous `{ id, label? }` placeholder to the
 * full `Address` shape (camelCase, mirrors `AddressDTO::toArray()`).
 * Existing call sites that only read `id` / `label` continue to work
 * because `Address` is a strict superset of the placeholder.
 */
export type AccountAddressSummary = Address;

/**
 * Re-export the canonical payment-method DTO under a slice-local name so
 * the rest of this module reads as Redux-state types rather than reaching
 * back into the API module. SF-ACCT-006 promoted the placeholder
 * `AccountPaymentMethodSummary` to the canonical `PaymentMethodDTO` shape.
 *
 * Kept as a `type` alias so other modules can `import {
 * AccountPaymentMethodSummary }` without churn — the legacy name now points
 * at the canonical DTO.
 */
export type AccountPaymentMethodSummary = PaymentMethodDTO;

/** Status of a per-address mutation (set-default, delete). Keyed by id. */
export type AddressActionStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

/**
 * Profile-form failure shape. Extends the section error with a
 * field-keyed validation map so the form can surface per-field errors
 * inline (`first_name`, `email`, etc.) on a 422 envelope.
 *
 * SF-ACCT-008.
 */
export interface AccountProfileUpdateError extends AccountSectionError {
  /** Field-keyed validation errors from the platform's `errors` map. */
  fieldErrors?: Record<string, string[]>;
}

export interface AccountState {
  recentOrders: AccountOrderSummary[];
  recentOrdersStatus: AccountSectionStatus;
  recentOrdersError: AccountSectionError | null;

  // ── SF-ACCT-002: full paginated order history page ────────────────
  /**
   * Full paginated orders list for the order-history page (SF-ACCT-002).
   * Disjoint from {@link AccountState.recentOrders} (the dashboard's
   * `limit=5` snapshot) so each page fetches with its own filters
   * without stomping the other.
   */
  orders: AccountOrderSummary[];
  /** Pagination envelope from the most recent {@link fetchOrders} response. */
  ordersMeta: AccountPaginationMeta | null;
  /** Lifecycle status of {@link fetchOrders}. */
  ordersStatus: AccountSectionStatus;
  /** Last error from {@link fetchOrders}; cleared on the next pending. */
  ordersError: AccountSectionError | null;

  /** `meta.total` from the artwork endpoint. `null` until the first fetch resolves. */
  artworkCount: number | null;
  artworkStatus: AccountSectionStatus;
  artworkError: AccountSectionError | null;

  // ── SF-ACCT-005: artwork library page ────────────────────────────────
  /**
   * Full paginated artwork-file rows for the library page. Disjoint from
   * `artworkCount` (which is the dashboard's `meta.total`-only snapshot)
   * so each page can fetch on its own cadence without stomping the other.
   */
  artworkFiles: AccountArtworkSummary[];
  artworkFilesMeta: AccountPaginationMeta | null;
  artworkFilesStatus: AccountSectionStatus;
  artworkFilesError: AccountSectionError | null;
  /**
   * Set when an optimistic `deleteArtworkFile` rolls back — e.g. on a 409
   * conflict the file remains in the list and the error message lands
   * here so the view can render an `Alert` listing the referenced
   * orders.
   */
  artworkDeleteError: AccountArtworkDeleteError | null;

  /**
   * Saved addresses. Populated by SF-ACCT-007. Empty array means the
   * user has not visited the addresses page yet OR has no addresses —
   * the completion checklist treats both states as "incomplete".
   */
  addresses: AccountAddressSummary[];
  /** Independent loading status for `fetchAddresses`. */
  addressesStatus: AccountSectionStatus;
  addressesError: AccountSectionError | null;

  /** Status of the create/update form (shared between create + update). */
  addressFormStatus: AccountSectionStatus;
  addressFormError: AccountSectionError | null;

  /**
   * Per-card mutation status keyed by address id. Used by the address
   * book view to render an inline spinner on the Set-Default / Delete
   * button without disabling other cards.
   */
  addressActionStatus: Record<number, AddressActionStatus>;
  /** Per-card error message keyed by address id. */
  addressActionError: Record<number, AccountSectionError | null>;

  /**
   * Snapshot of the address id that held `isDefaultShipping = true`
   * before the current optimistic `setDefaultAddress` started. `null`
   * when no optimistic action is in flight or no prior default existed.
   * The `rejected` reducer reads this to roll the badge back.
   */
  defaultAddressIdBeforeOptimistic: number | null;

  /**
   * Saved Stripe payment methods. Populated by SF-ACCT-006's
   * `fetchPaymentMethods` thunk. Same emptiness semantics as `addresses`
   * — empty means "user has not visited the page yet OR has no cards".
   */
  paymentMethods: PaymentMethodDTO[];

  /** List-fetch status for {@link fetchPaymentMethods}. */
  paymentMethodsStatus: AccountSectionStatus;
  /** List-fetch error envelope, when {@link fetchPaymentMethods} fails. */
  paymentMethodsError: AccountSectionError | null;

  /**
   * `addPaymentMethod` lifecycle is independent of the list-fetch so the
   * Add Card form can show its own spinner while the list re-fetches in
   * the background after Stripe confirmation.
   */
  addPaymentMethodStatus: AccountSectionStatus;
  addPaymentMethodError: AccountSectionError | null;

  /**
   * Per-card action state, keyed by Stripe PaymentMethod id (`pm_xxx`).
   * A card row inspects the value at its own id to render an inline
   * spinner on its own button without disabling sibling cards.
   *
   * Set-default and remove share the same map because only one of the
   * two can be in flight on a given card at a time (the UI disables the
   * other while either is pending).
   */
  paymentMethodActionStatus: Record<string, AccountSectionStatus>;

  /**
   * Per-card action errors, keyed by Stripe PaymentMethod id. Cleared
   * when the next action on the same card begins so the UI doesn't
   * surface stale failures.
   */
  paymentMethodActionError: Record<string, AccountSectionError | null>;

  /**
   * Order id whose reorder thunk is currently in flight, or `null` when
   * no reorder is dispatching.
   */
  reorderInFlightId: number | null;

  // ── SF-ACCT-004: reorder lifecycle ────────────────────────────────
  /**
   * Lifecycle status of the most recent reorder dispatch.
   *
   *  - `idle`     — no reorder has been attempted, or the previous one
   *                 has been cleared via `clearReorderState`.
   *  - `loading`  — at least one cart-add call is in flight.
   *  - `success`  — every line item was added; container has navigated
   *                 to `/cart` and shown the success toast.
   *  - `partial`  — at least one cart-add succeeded and at least one
   *                 failed; container surfaces the partial-failure modal
   *                 with the list at {@link reorderUnrestorable}.
   *  - `failed`   — every cart-add failed (or the order itself failed
   *                 to load); container surfaces the error toast.
   */
  reorderStatus: 'idle' | 'loading' | 'success' | 'partial' | 'failed';
  /**
   * Names of the order items that could not be restored on the most
   * recent partial-failure dispatch. Read by `ReorderPartialModal`.
   * Empty array on `idle` / `loading` / `success`; populated on
   * `partial` and `failed`.
   */
  reorderUnrestorable: { id: number; name: string }[];
  /**
   * Cart item ids that were added during the most recent partial
   * dispatch. Captured here so the `Cancel` path on the modal can issue
   * one `DELETE /cart/items/:id` per added line, leaving the cart in
   * its pre-reorder state. Reset to `[]` on every new reorder dispatch.
   */
  reorderAddedCartItemIds: number[];
  /**
   * Last error message surfaced by the reorder thunk on a full-failure
   * path. Read by the container to surface a toast. `null` while the
   * thunk is idle / loading or when the most recent dispatch was a
   * partial / full success.
   */
  reorderError: AccountSectionError | null;

  // ── SF-ACCT-012: cancel-reorder lifecycle ─────────────────────────
  /** Whether `cancelPartialReorder` is currently in flight. */
  cancelReorderLoading: boolean;
  /** Last error from `cancelPartialReorder`; `null` on idle or success. */
  cancelReorderError: AccountSectionError | null;

  // ── SF-ACCT-003: single-order detail page ──────────────────────────
  /**
   * Currently-loaded order detail for the SF-ACCT-003 detail page.
   * Disjoint from {@link AccountState.orders} (the list page's
   * paginated summaries) so a slow detail fetch never blocks the list,
   * and so re-visiting the list after viewing a detail page doesn't
   * trigger a re-render storm.
   *
   * `null` until the first {@link fetchOrder} resolves, OR when the
   * detail page unmounts and dispatches {@link clearCurrentOrder}.
   */
  currentOrder: AccountOrderDetail | null;
  /** Lifecycle status of {@link fetchOrder}. */
  currentOrderStatus: AccountSectionStatus;
  /** Last error from {@link fetchOrder}; cleared on the next pending. */
  currentOrderError: AccountSectionError | null;

  // ── SF-ACCT-008: profile settings page ─────────────────────────────
  /**
   * Authenticated customer's full profile, hydrated from
   * `GET /api/v1/account` on first visit to `/account/profile`.
   *
   * `null` until the first fetch resolves — the view renders a skeleton
   * while in `idle` / `loading`. Stays populated after a successful
   * `updateProfile` so revisits don't re-fetch unless explicitly retried.
   */
  profile: AccountProfile | null;
  profileStatus: AccountSectionStatus;
  profileError: AccountSectionError | null;

  /**
   * Independent lifecycle for the `updateProfile` mutation so the page
   * can disable the submit button / show a spinner without affecting
   * the read-side `profileStatus`.
   */
  updateProfileStatus: AccountSectionStatus;
  updateProfileError: AccountProfileUpdateError | null;
}

const initialState: AccountState = {
  recentOrders: [],
  recentOrdersStatus: 'idle',
  recentOrdersError: null,

  orders: [],
  ordersMeta: null,
  ordersStatus: 'idle',
  ordersError: null,

  artworkCount: null,
  artworkStatus: 'idle',
  artworkError: null,

  artworkFiles: [],
  artworkFilesMeta: null,
  artworkFilesStatus: 'idle',
  artworkFilesError: null,
  artworkDeleteError: null,

  addresses: [],
  addressesStatus: 'idle',
  addressesError: null,
  addressFormStatus: 'idle',
  addressFormError: null,
  addressActionStatus: {},
  addressActionError: {},
  defaultAddressIdBeforeOptimistic: null,

  paymentMethods: [],
  paymentMethodsStatus: 'idle',
  paymentMethodsError: null,
  addPaymentMethodStatus: 'idle',
  addPaymentMethodError: null,
  paymentMethodActionStatus: {},
  paymentMethodActionError: {},

  reorderInFlightId: null,
  reorderStatus: 'idle',
  reorderUnrestorable: [],
  reorderAddedCartItemIds: [],
  reorderError: null,
  cancelReorderLoading: false,
  cancelReorderError: null,

  currentOrder: null,
  currentOrderStatus: 'idle',
  currentOrderError: null,

  profile: null,
  profileStatus: 'idle',
  profileError: null,
  updateProfileStatus: 'idle',
  updateProfileError: null,
};

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

function toSectionError(err: unknown): AccountSectionError {
  if (isApiError(err)) {
    const apiErr = err as ApiError;
    return {
      message: apiErr.message,
      status: apiErr.status,
      code: apiErr.errorCode,
    };
  }
  return {
    message: err instanceof Error ? err.message : 'Request failed',
  };
}

// ----------------------------------------------------------------------------
// Thunks
// ----------------------------------------------------------------------------

/**
 * Fetch the authenticated customer's recent orders. Dashboard passes
 * `{ limit: 5, page: 1 }`.
 */
export const fetchRecentOrders = createAsyncThunk(
  'account/fetchRecentOrders',
  async (params: GetAccountOrdersParams = { limit: 5, page: 1 }, { rejectWithValue }) => {
    try {
      return await accountApi.getAccountOrders(params);
    } catch (err) {
      return rejectWithValue(toSectionError(err));
    }
  },
);

/**
 * Fetch the authenticated customer's full paginated order history for
 * the SF-ACCT-002 list page. Distinct from {@link fetchRecentOrders}
 * (which is the dashboard's `limit=5` snapshot) so each page owns its
 * own loading + error state without ripple effects.
 *
 * Default args mirror the page's first mount with no URL filters
 * applied; {@link AccountState.orders} is populated on success and
 * {@link AccountState.ordersMeta} carries pagination + total counts.
 */
export const fetchOrders = createAsyncThunk(
  'account/fetchOrders',
  async (params: GetAccountOrdersParams = { limit: 20, page: 1 }, { rejectWithValue }) => {
    try {
      return await accountApi.getAccountOrders(params);
    } catch (err) {
      return rejectWithValue(toSectionError(err));
    }
  },
);

/**
 * Fetch the artwork count via `/account/artwork?limit=1`. We only need
 * `meta.total` for the dashboard stat card so the smallest reasonable
 * page size keeps the payload minimal.
 */
export const fetchArtworkCount = createAsyncThunk(
  'account/fetchArtworkCount',
  async (params: GetAccountArtworkParams = { limit: 1, page: 1 }, { rejectWithValue }) => {
    try {
      return await accountApi.getAccountArtwork(params);
    } catch (err) {
      return rejectWithValue(toSectionError(err));
    }
  },
);

// ----------------------------------------------------------------------------
// Payment-method thunks (SF-ACCT-006)
// ----------------------------------------------------------------------------

/**
 * Fetch the authenticated customer's saved Stripe payment methods.
 *
 * No params — the platform scopes results to the auth context. Returns
 * the bare list; the slice extracts `paymentMethods` into state.
 */
export const fetchPaymentMethods = createAsyncThunk(
  'account/fetchPaymentMethods',
  async (_: void | undefined, { rejectWithValue }) => {
    try {
      return await accountApi.getAccountPaymentMethods();
    } catch (err) {
      return rejectWithValue(toSectionError(err));
    }
  },
);

/**
 * Set a saved card as the customer's default payment method.
 *
 * The thunk dispatches per-card spinners via {@link
 * AccountState.paymentMethodActionStatus} and updates the in-place list
 * on success without re-fetching — so the Default badge transfers
 * immediately.
 */
export const setDefaultPaymentMethod = createAsyncThunk<
  PaymentMethodDTO,
  string,
  { rejectValue: AccountSectionError; rejectMeta: { id: string } }
>(
  'account/setDefaultPaymentMethod',
  async (id, { rejectWithValue }) => {
    try {
      const { paymentMethod } = await accountApi.setDefaultAccountPaymentMethod(id);
      return paymentMethod;
    } catch (err) {
      return rejectWithValue(toSectionError(err));
    }
  },
);

/**
 * Detach a saved card from the customer's Stripe account.
 *
 * The platform 409s when the card is the default and the customer's only
 * card; the UI prevents that case at the dialog level, but the slice
 * still preserves the original card in state on failure so a stale 409
 * never silently disappears the row.
 */
export const removePaymentMethod = createAsyncThunk<
  string,
  string,
  { rejectValue: AccountSectionError; rejectMeta: { id: string } }
>(
  'account/removePaymentMethod',
  async (id, { rejectWithValue }) => {
    try {
      await accountApi.deleteAccountPaymentMethod(id);
      return id;
    } catch (err) {
      return rejectWithValue(toSectionError(err));
    }
  },
);

/**
 * Result returned by the consumer-supplied Stripe confirmation callback.
 * Either `error` (from Stripe.js — `error.message` surfaces inline below
 * the CardElement) or `setupIntent` (success) is set; both being absent
 * is treated as failure.
 */
export interface AddPaymentMethodConfirmResult {
  error?: { message?: string; code?: string };
  setupIntent?: { id?: string; status?: string };
}

/**
 * Add a new saved card to the customer's Stripe account.
 *
 * The thunk:
 *   1. Calls `POST /api/v1/account/payment-methods` to receive a
 *      SetupIntent `clientSecret`.
 *   2. Invokes the consumer-supplied `confirm` callback — typically
 *      `(secret) => stripe.confirmCardSetup(secret, { payment_method:
 *      { card: cardElement } })`. The callback returns Stripe's standard
 *      `{ error?, setupIntent? }` result shape.
 *   3. On Stripe success syncs the confirmed SetupIntent to the platform
 *      (best effort — a failure is swallowed because the webhook may still
 *      land), then re-dispatches `fetchPaymentMethods` and
 *      resolves; on Stripe error rejects with the Stripe `message` text
 *      so the form can surface it inline below the CardElement.
 *
 * Stripe-js types stay out of the slice on purpose — taking a callback
 * means `@stripe/stripe-js` and `@stripe/react-stripe-js` are imported
 * only inside the lazy `AddPaymentMethodForm` capsule and never enter
 * the slice's module graph. The slice file (and the rest of the
 * customer-portal bundle) ships without the ~80KB Stripe.js dependency.
 */
export interface AddPaymentMethodArgs {
  /**
   * Async callback that runs Stripe confirmation against the issued
   * client secret. Implementations typically call
   * `stripe.confirmCardSetup(clientSecret, …)` and return its result.
   */
  confirm: (clientSecret: string) => Promise<AddPaymentMethodConfirmResult>;
}

export const addPaymentMethod = createAsyncThunk<
  void,
  AddPaymentMethodArgs,
  { rejectValue: AccountSectionError }
>(
  'account/addPaymentMethod',
  async ({ confirm }, { dispatch, rejectWithValue }) => {
    try {
      const { clientSecret } =
        await accountApi.createPaymentMethodSetupIntent();
      const result = await confirm(clientSecret);
      if (result.error) {
        return rejectWithValue({
          message: result.error.message ?? 'Card could not be saved.',
          code: result.error.code,
        });
      }
      if (!result.setupIntent) {
        return rejectWithValue({
          message: 'Stripe did not return a confirmation result.',
        });
      }
      // Best effort: if the sync fails the webhook may still land, and the
      // re-fetch below is the source of truth for whether the card exists.
      if (result.setupIntent.id) {
        try {
          await accountApi.syncAccountPaymentMethod(result.setupIntent.id);
        } catch {
          // Falls through to the list re-fetch.
        }
      }
      await dispatch(fetchPaymentMethods());
    } catch (err) {
      return rejectWithValue(toSectionError(err));
    }
  },
);

/**
 * Fetch the authenticated customer's full paginated artwork-file list
 * for the SF-ACCT-005 library page. Distinct from `fetchArtworkCount`
 * (which only consumes `meta.total`) so each page owns its own
 * loading + error state without ripple effects.
 */
export const fetchArtworkFiles = createAsyncThunk(
  'account/fetchArtworkFiles',
  async (params: GetAccountArtworkParams = { limit: 24, page: 1 }, { rejectWithValue }) => {
    try {
      return await accountApi.getAccountArtwork(params);
    } catch (err) {
      return rejectWithValue(toSectionError(err));
    }
  },
);

/**
 * Argument shape accepted by `deleteArtworkFile`. The thunk needs the
 * `id` to fire the DELETE request and the full snapshot to support
 * rollback when the call rejects (the slice removes the entry
 * optimistically inside the pending reducer).
 */
export interface DeleteArtworkFileArg {
  id: number;
  /**
   * Snapshot of the file as it existed in the list before optimistic
   * removal. Restored verbatim on rejection so we don't need to
   * re-fetch the page just to bring the row back.
   */
  snapshot: AccountArtworkSummary;
}

/**
 * Delete an artwork file owned by the authenticated customer. Behaviour:
 *  - Pending: the slice's pending reducer optimistically removes the
 *    file from `artworkFiles` and clears any prior delete error.
 *  - Fulfilled: the optimistic removal sticks; meta totals decrement.
 *  - Rejected: the file is restored to its original index, and the
 *    `artworkDeleteError` field is populated with the platform's
 *    message — including the `referencedOrderNumbers` list when the
 *    rejection was a 409 conflict.
 *
 * Pre-rule note: per `.claude/rules/frontend-data-flow.md`, the toast /
 * navigation side effects belong in the container, not in the slice.
 * This thunk only persists the rollback shape.
 */
export const deleteArtworkFile = createAsyncThunk<
  { id: number },
  DeleteArtworkFileArg,
  { rejectValue: AccountArtworkDeleteError }
>('account/deleteArtworkFile', async ({ id }, { rejectWithValue }) => {
  try {
    return await accountApi.deleteAccountArtwork(id);
  } catch (err) {
    const base = toSectionError(err);
    const referencedOrderNumbers = isApiError(err) ? extractReferencedOrderNumbers(err as ApiError) : undefined;
    return rejectWithValue({
      ...base,
      fileId: id,
      referencedOrderNumbers,
    });
  }
});

/**
 * The platform's 409 conflict body lives on the structured `error`
 * envelope slot under `error.context.referencedOrderNumbers`. The
 * envelope unwrapper lifts the message + code to the typed `ApiError`,
 * but the context payload is opaque to that helper — so we re-read it
 * here. Any shape mismatch quietly returns `undefined` (the alert just
 * renders a generic message rather than crashing the slice).
 */
function extractReferencedOrderNumbers(err: ApiError): string[] | undefined {
  const ctx = err.context;
  if (!ctx) return undefined;
  const raw = (ctx as Partial<DeleteAccountArtworkConflict>).referencedOrderNumbers;
  if (!Array.isArray(raw)) return undefined;
  return raw.filter((s): s is string => typeof s === 'string');
}

/**
 * Fetch a single order's full detail for the SF-ACCT-003 detail page.
 * The thunk hits `GET /api/v1/account/orders/:id`; the slice stores the
 * result on `currentOrder` and surfaces lifecycle status via
 * {@link AccountState.currentOrderStatus}.
 *
 * `clearCurrentOrder` is the canonical way to drop the loaded order
 * (called on detail-page unmount) so re-mounting the page on a different
 * id doesn't briefly render the previous order while the new fetch is
 * pending.
 */
export const fetchOrder = createAsyncThunk<
  AccountOrderDetail,
  number,
  { rejectValue: AccountSectionError }
>(
  'account/fetchOrder',
  async (id, { rejectWithValue }) => {
    try {
      return await accountApi.getAccountOrder(id);
    } catch (err) {
      return rejectWithValue(toSectionError(err));
    }
  },
);

/**
 * Outcome of a single cart-add attempt during a reorder dispatch. Lifted
 * out of the thunk body so reducers + selectors can spell out the union
 * without duplicating it.
 */
export interface ReorderItemOutcome {
  /** Source `AccountOrderItem.id` — kept so the modal can list rejects. */
  sourceItemId: number;
  /** Display name for the item, taken from `productSnapshot.name`. */
  name: string;
  /** Cart-item id when the add succeeded; `null` on failure. */
  cartItemId: number | null;
  /** Reason the add failed; `null` on success. */
  reason: string | null;
  /** True when the add succeeded. Mirrors `cartItemId !== null`. */
  ok: boolean;
}

export interface ReorderFromOrderResult {
  orderId: number;
  /**
   * Per-source-item outcomes in the same order they were dispatched.
   * Containers iterate this to drive their navigation + toast logic
   * (full success → /cart + success toast; partial → modal; full
   * failure → error toast).
   */
  outcomes: ReorderItemOutcome[];
}

/**
 * Quick-reorder thunk — asks the platform to rebuild the order's lines in
 * the cart (`POST /account/orders/:id/reorder`) and maps its `added` /
 * `unrestorable` lists to per-line outcomes. The reducer computes
 * `reorderStatus` (`success | partial | failed`) from the outcomes.
 *
 * Toast / navigation side effects belong in the container per
 * `.claude/rules/frontend-data-flow.md`. The thunk is pure data flow.
 */
export const reorderFromOrder = createAsyncThunk<
  ReorderFromOrderResult,
  number,
  { rejectValue: AccountSectionError }
>('account/reorderFromOrder', async (orderId, { rejectWithValue }) => {
  try {
    const result = await accountApi.reorderAccountOrder(orderId);
    const outcomes: ReorderItemOutcome[] = [
      ...result.added.map((line) => ({
        sourceItemId: line.order_item_id,
        name: line.name,
        cartItemId: line.cart_item_id,
        reason: null,
        ok: true,
      })),
      ...result.unrestorable.map((line) => ({
        sourceItemId: line.order_item_id,
        name: line.name,
        cartItemId: null,
        reason: line.reason,
        ok: false,
      })),
    ];
    return { orderId, outcomes };
  } catch (err) {
    return rejectWithValue(toSectionError(err));
  }
});

/**
 * Argument shape for {@link cancelPartialReorder}. The container hands
 * back the cart-item ids that were added during the partial dispatch
 * so the thunk can issue one delete per line. Captured separately
 * (rather than read from state) so the action remains a pure value
 * payload.
 */
export interface CancelPartialReorderArgs {
  cartItemIds: number[];
}

/**
 * Discard the items that were added during a partial-restore reorder.
 * Issues `DELETE /cart/items/:id` for each, in parallel via
 * `Promise.allSettled`. Failures are silently absorbed (the cancel
 * path is best-effort — if the cart-item delete fails the customer
 * lands on a cart that still has the line, which is recoverable from
 * the cart UI itself once it ships).
 */
export const cancelPartialReorder = createAsyncThunk<
  void,
  CancelPartialReorderArgs
>('account/cancelPartialReorder', async ({ cartItemIds }) => {
  if (cartItemIds.length === 0) return;
  await Promise.allSettled(
    cartItemIds.map((id) => cartApi.deleteCartItemApi(id)),
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// Address-book thunks (SF-ACCT-007)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch the customer's saved shipping addresses. Dispatched on
 * AddressesView mount and from the retry button when the previous
 * attempt failed.
 */
export const fetchAddresses = createAsyncThunk(
  'account/fetchAddresses',
  async (_: void, { rejectWithValue }) => {
    try {
      return await accountApi.getAccountAddresses();
    } catch (err) {
      return rejectWithValue(toSectionError(err));
    }
  },
);

/**
 * Create a new saved address. Resolves with the persisted record so the
 * slice can append it to `addresses`.
 */
export const createAddress = createAsyncThunk(
  'account/createAddress',
  async (payload: AddressInput, { rejectWithValue }) => {
    try {
      return await accountApi.createAccountAddress(payload);
    } catch (err) {
      return rejectWithValue(toSectionError(err));
    }
  },
);

/**
 * Update an existing saved address. The slice replaces the in-state
 * record with the server-canonical version returned in the response.
 */
export const updateAddress = createAsyncThunk(
  'account/updateAddress',
  async (
    args: { id: number; payload: AddressInput },
    { rejectWithValue },
  ) => {
    try {
      return await accountApi.updateAccountAddress(args.id, args.payload);
    } catch (err) {
      return rejectWithValue(toSectionError(err));
    }
  },
);

/**
 * Soft-delete an address. The server returns 204 with no body; this
 * thunk resolves with `{ id }` so the reducer can drop the matching
 * row from `addresses`.
 */
export const deleteAddress = createAsyncThunk(
  'account/deleteAddress',
  async (id: number, { rejectWithValue }) => {
    try {
      return await accountApi.deleteAccountAddress(id);
    } catch (err) {
      return rejectWithValue(toSectionError(err));
    }
  },
);

/**
 * Promote an address as the customer's default shipping address.
 *
 * Optimistic update: the `pending` reducer flips `isDefaultShipping`
 * across the in-state addresses immediately (target → true, siblings →
 * false). The previous snapshot is captured on the action's `meta` so
 * the `rejected` reducer can roll back. This way the badge moves before
 * the server responds and rolls back cleanly when the request fails.
 */
export const setDefaultAddress = createAsyncThunk<
  Awaited<ReturnType<typeof accountApi.setDefaultAccountAddress>>,
  number,
  { state: { account: AccountState }; rejectValue: AccountSectionError }
>(
  'account/setDefaultAddress',
  async (id, { rejectWithValue }) => {
    try {
      return await accountApi.setDefaultAccountAddress(id);
    } catch (err) {
      return rejectWithValue(toSectionError(err));
    }
  },
);

export const setDefaultBillingAddress = createAsyncThunk<
  Awaited<ReturnType<typeof accountApi.setDefaultAccountAddress>>,
  number,
  { state: { account: AccountState }; rejectValue: AccountSectionError }
>(
  'account/setDefaultBillingAddress',
  async (id, { rejectWithValue }) => {
    try {
      return await accountApi.setDefaultAccountAddress(id, 'billing');
    } catch (err) {
      return rejectWithValue(toSectionError(err));
    }
  },
);

// ─────────────────────────────────────────────────────────────────────────────
// Profile thunks (SF-ACCT-008)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch the authenticated customer's full profile (`GET /api/v1/account`).
 *
 * Dispatched by `ProfileView` on mount when `profileStatus === 'idle'`
 * and from the retry button when the previous attempt failed. The
 * `updateProfile` thunk replaces `profile` on success, so re-visiting
 * the page in the same session does not re-fetch.
 */
export const fetchProfile = createAsyncThunk(
  'account/fetchProfile',
  async (_: void | undefined, { rejectWithValue }) => {
    try {
      return await accountApi.getAccountProfile();
    } catch (err) {
      return rejectWithValue(toSectionError(err));
    }
  },
);

/**
 * Update the authenticated customer's profile (`PUT /api/v1/account`).
 *
 * On success the slice replaces `profile` with the server-canonical
 * shape and the consumer (the view) optimistically merges the changed
 * `firstName` / `lastName` / `email` into `authSlice.user` via the
 * exported `updateUserProfile` reducer. Toast / navigation side effects
 * stay in the view per `.claude/rules/frontend-data-flow.md`.
 *
 * Rejection payload is the slice's {@link AccountProfileUpdateError},
 * which preserves the platform's `errors` map so the form can call
 * `form.setError(field, …)` per server-rejected field.
 */
export const updateProfile = createAsyncThunk<
  AccountProfile,
  AccountProfileUpdateInput,
  { rejectValue: AccountProfileUpdateError }
>('account/updateProfile', async (payload, { rejectWithValue }) => {
  try {
    return await accountApi.updateAccountProfile(payload);
  } catch (err) {
    const base = toSectionError(err);
    const fieldErrors = isApiError(err)
      ? (err as ApiError).errors
      : undefined;
    return rejectWithValue({ ...base, fieldErrors });
  }
});

// ----------------------------------------------------------------------------
// Slice
// ----------------------------------------------------------------------------

const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    /**
     * Replace the saved-addresses list. Called by SF-ACCT-006's
     * fetch-addresses thunk; available now so tests can seed state.
     */
    setAddresses(state, action: PayloadAction<AccountAddressSummary[]>) {
      state.addresses = action.payload;
    },
    /**
     * Replace the saved payment methods list. Called by tests + by the
     * dashboard's pre-seed flow; the canonical writer is the
     * `fetchPaymentMethods` thunk.
     */
    setPaymentMethods(state, action: PayloadAction<PaymentMethodDTO[]>) {
      state.paymentMethods = action.payload;
    },
    /**
     * Reset the Add Card form's status to `idle`. Called when the dialog
     * unmounts, when the customer dismisses an inline Stripe error, or
     * after a successful add so subsequent attempts start clean.
     */
    clearAddPaymentMethodError(state) {
      state.addPaymentMethodStatus = 'idle';
      state.addPaymentMethodError = null;
    },
    /**
     * Reset the slice to its initial shape. Logout flows fan this out
     * alongside `auth/logout` so a re-login doesn't leak the previous
     * session's account data.
     */
    resetAccountState() {
      return initialState;
    },
    /**
     * Reset the create/update form status — called by the address book
     * view when the slide-over drawer opens or closes so a previous
     * error doesn't bleed into the next session.
     */
    resetAddressFormStatus(state) {
      state.addressFormStatus = 'idle';
      state.addressFormError = null;
    },
    /**
     * Clear the per-card error message for a single address — called by
     * the view after the customer dismisses an inline error.
     */
    clearAddressActionError(state, action: PayloadAction<number>) {
      delete state.addressActionError[action.payload];
      if (state.addressActionStatus[action.payload] === 'failed') {
        delete state.addressActionStatus[action.payload];
      }
    },
    /**
     * Clear the most recent artwork delete error. The view dismisses the
     * `Alert` after it's been seen / acted on; the slice doesn't auto-
     * clear on subsequent fetches because the error is informational.
     */
    clearArtworkDeleteError(state) {
      state.artworkDeleteError = null;
    },
    /**
     * Prepend a freshly-uploaded artwork file to the in-memory library
     * list — used by SF-FILES-001's inline FileUploader so the new card
     * appears at the top of the grid without a full refetch. Increments
     * the meta totals so the pagination footer stays accurate.
     *
     * If a file with the same id already exists in `artworkFiles`
     * (defensive against a double-dispatch), the entry is replaced in
     * place rather than duplicated.
     */
    prependArtworkFile(state, action: PayloadAction<AccountArtworkSummary>) {
      const incoming = action.payload;
      const existingIdx = state.artworkFiles.findIndex((f) => f.id === incoming.id);
      if (existingIdx === -1) {
        state.artworkFiles = [incoming, ...state.artworkFiles];
        if (state.artworkFilesMeta) {
          const nextTotal = state.artworkFilesMeta.total + 1;
          const limit = state.artworkFilesMeta.limit > 0 ? state.artworkFilesMeta.limit : 24;
          state.artworkFilesMeta = {
            ...state.artworkFilesMeta,
            total: nextTotal,
            lastPage: Math.max(1, Math.ceil(nextTotal / limit)),
          };
        }
      } else {
        state.artworkFiles[existingIdx] = incoming;
      }
    },
    /**
     * Reset the {@link updateProfile} mutation status / error — called
     * by the view when the form is re-submitted or when the customer
     * dismisses an inline form-level error. Independent of the read-side
     * `profileStatus` so a clear here never wipes the loaded profile.
     */
    clearUpdateProfileError(state) {
      state.updateProfileStatus = 'idle';
      state.updateProfileError = null;
    },
    /**
     * Drop the currently-loaded order detail and reset the lifecycle
     * status. Called by the SF-ACCT-003 detail page on unmount so the
     * next mount starts from a clean `idle` state instead of briefly
     * flashing the previous order while the new fetch is pending.
     */
    clearCurrentOrder(state) {
      state.currentOrder = null;
      state.currentOrderStatus = 'idle';
      state.currentOrderError = null;
    },
    /**
     * Reset the SF-ACCT-004 reorder lifecycle to `idle`. Dispatched by
     * the partial-failure modal on close (after the customer chose
     * Continue or Cancel) and by the container after surfacing the
     * full-failure error toast so a subsequent reorder attempt starts
     * from a clean slate.
     */
    clearReorderState(state) {
      state.reorderStatus = 'idle';
      state.reorderUnrestorable = [];
      state.reorderAddedCartItemIds = [];
      state.reorderError = null;
    },
  },
  extraReducers(builder) {
    builder
      // -- fetchRecentOrders --------------------------------------------
      .addCase(fetchRecentOrders.pending, (state) => {
        state.recentOrdersStatus = 'loading';
        state.recentOrdersError = null;
      })
      .addCase(fetchRecentOrders.fulfilled, (state, action) => {
        state.recentOrdersStatus = 'succeeded';
        state.recentOrders = action.payload.orders;
      })
      .addCase(fetchRecentOrders.rejected, (state, action) => {
        state.recentOrdersStatus = 'failed';
        const payload = action.payload as AccountSectionError | undefined;
        state.recentOrdersError = payload ?? {
          message: action.error.message ?? 'Failed to load recent orders',
        };
      })
      // -- fetchOrders (SF-ACCT-002 — full paginated history) -----------
      .addCase(fetchOrders.pending, (state) => {
        state.ordersStatus = 'loading';
        state.ordersError = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.ordersStatus = 'succeeded';
        state.orders = action.payload.orders;
        state.ordersMeta = action.payload.meta;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.ordersStatus = 'failed';
        const payload = action.payload as AccountSectionError | undefined;
        state.ordersError = payload ?? {
          message: action.error.message ?? 'Failed to load orders',
        };
      })
      // -- fetchArtworkCount --------------------------------------------
      .addCase(fetchArtworkCount.pending, (state) => {
        state.artworkStatus = 'loading';
        state.artworkError = null;
      })
      .addCase(fetchArtworkCount.fulfilled, (state, action) => {
        state.artworkStatus = 'succeeded';
        state.artworkCount = action.payload.meta?.total ?? null;
      })
      .addCase(fetchArtworkCount.rejected, (state, action) => {
        state.artworkStatus = 'failed';
        const payload = action.payload as AccountSectionError | undefined;
        state.artworkError = payload ?? {
          message: action.error.message ?? 'Failed to load artwork',
        };
      })
      // -- fetchArtworkFiles --------------------------------------------
      .addCase(fetchArtworkFiles.pending, (state) => {
        state.artworkFilesStatus = 'loading';
        state.artworkFilesError = null;
      })
      .addCase(fetchArtworkFiles.fulfilled, (state, action) => {
        state.artworkFilesStatus = 'succeeded';
        state.artworkFiles = action.payload.files;
        state.artworkFilesMeta = action.payload.meta;
      })
      .addCase(fetchArtworkFiles.rejected, (state, action) => {
        state.artworkFilesStatus = 'failed';
        const payload = action.payload as AccountSectionError | undefined;
        state.artworkFilesError = payload ?? {
          message: action.error.message ?? 'Failed to load artwork files',
        };
      })
      // -- deleteArtworkFile (optimistic) -------------------------------
      .addCase(deleteArtworkFile.pending, (state, action) => {
        const targetId = action.meta.arg.id;
        state.artworkDeleteError = null;
        state.artworkFiles = state.artworkFiles.filter((f) => f.id !== targetId);
        if (state.artworkFilesMeta) {
          state.artworkFilesMeta = {
            ...state.artworkFilesMeta,
            total: Math.max(0, state.artworkFilesMeta.total - 1),
            lastPage:
              state.artworkFilesMeta.lastPage !== undefined
                ? Math.max(1, Math.ceil(Math.max(0, state.artworkFilesMeta.total - 1) / state.artworkFilesMeta.limit))
                : state.artworkFilesMeta.lastPage,
          };
        }
        // Mirror the dashboard's count when the user has both the
        // dashboard mounted in another tab + the library mounted here.
        if (state.artworkCount !== null) {
          state.artworkCount = Math.max(0, state.artworkCount - 1);
        }
      })
      .addCase(deleteArtworkFile.fulfilled, (state) => {
        // Optimistic removal already happened in `pending`. No-op aside
        // from any future side effects (audit, telemetry).
        state.artworkDeleteError = null;
      })
      .addCase(deleteArtworkFile.rejected, (state, action) => {
        // Restore the snapshot at its original index so the row appears
        // exactly where it was — preserves visual stability when the
        // delete was triggered from the middle of the page.
        const { id, snapshot } = action.meta.arg;
        const alreadyPresent = state.artworkFiles.some((f) => f.id === id);
        if (!alreadyPresent) {
          state.artworkFiles = [...state.artworkFiles, snapshot].sort((a, b) => b.id - a.id);
          if (state.artworkFilesMeta) {
            state.artworkFilesMeta = {
              ...state.artworkFilesMeta,
              total: state.artworkFilesMeta.total + 1,
              lastPage:
                state.artworkFilesMeta.lastPage !== undefined
                  ? Math.max(1, Math.ceil((state.artworkFilesMeta.total + 1) / state.artworkFilesMeta.limit))
                  : state.artworkFilesMeta.lastPage,
            };
          }
          if (state.artworkCount !== null) {
            state.artworkCount = state.artworkCount + 1;
          }
        }

        const payload = action.payload as AccountArtworkDeleteError | undefined;
        state.artworkDeleteError = payload ?? {
          message: action.error.message ?? 'Failed to delete artwork file',
          fileId: id,
        };
      })
      // -- fetchOrder (SF-ACCT-003 — single-order detail) ---------------
      .addCase(fetchOrder.pending, (state) => {
        state.currentOrderStatus = 'loading';
        state.currentOrderError = null;
      })
      .addCase(fetchOrder.fulfilled, (state, action) => {
        state.currentOrderStatus = 'succeeded';
        state.currentOrder = action.payload;
      })
      .addCase(fetchOrder.rejected, (state, action) => {
        state.currentOrderStatus = 'failed';
        const payload = action.payload as AccountSectionError | undefined;
        state.currentOrderError = payload ?? {
          message: action.error.message ?? 'Failed to load order',
        };
      })
      // -- reorderFromOrder (SF-ACCT-004) -------------------------------
      .addCase(reorderFromOrder.pending, (state, action) => {
        state.reorderInFlightId = action.meta.arg;
        state.reorderStatus = 'loading';
        state.reorderUnrestorable = [];
        state.reorderAddedCartItemIds = [];
        state.reorderError = null;
      })
      .addCase(reorderFromOrder.fulfilled, (state, action) => {
        state.reorderInFlightId = null;
        const outcomes = action.payload.outcomes;
        const ok = outcomes.filter((o) => o.ok);
        const failed = outcomes.filter((o) => !o.ok);
        state.reorderAddedCartItemIds = ok
          .map((o) => o.cartItemId)
          .filter((id): id is number => id !== null);
        state.reorderUnrestorable = failed.map((o) => ({
          id: o.sourceItemId,
          name: o.name,
        }));

        if (outcomes.length === 0) {
          // No items at all — surface as a failure so the container can
          // toast rather than navigate to an empty cart.
          state.reorderStatus = 'failed';
          state.reorderError = {
            message: 'This order has no items to reorder.',
          };
          return;
        }
        if (ok.length === 0) {
          state.reorderStatus = 'failed';
          state.reorderError = {
            message: 'Unable to reorder — all items are no longer available.',
          };
          return;
        }
        state.reorderStatus = failed.length === 0 ? 'success' : 'partial';
        state.reorderError = null;
      })
      .addCase(reorderFromOrder.rejected, (state, action) => {
        state.reorderInFlightId = null;
        state.reorderStatus = 'failed';
        state.reorderUnrestorable = [];
        state.reorderAddedCartItemIds = [];
        const payload = action.payload as AccountSectionError | undefined;
        state.reorderError = payload ?? {
          message: action.error.message ?? 'Failed to reorder',
        };
      })
      // -- cancelPartialReorder -----------------------------------------
      .addCase(cancelPartialReorder.pending, (state) => {
        state.cancelReorderLoading = true;
        state.cancelReorderError = null;
      })
      .addCase(cancelPartialReorder.fulfilled, (state) => {
        state.cancelReorderLoading = false;
        state.cancelReorderError = null;
        state.reorderStatus = 'idle';
        state.reorderUnrestorable = [];
        state.reorderAddedCartItemIds = [];
        state.reorderError = null;
      })
      .addCase(cancelPartialReorder.rejected, (state, action) => {
        state.cancelReorderLoading = false;
        state.cancelReorderError = {
          message: action.error.message ?? 'Failed to cancel reorder',
        };
      })
      // -- fetchAddresses (SF-ACCT-007) --------------------------------
      .addCase(fetchAddresses.pending, (state) => {
        state.addressesStatus = 'loading';
        state.addressesError = null;
      })
      .addCase(fetchAddresses.fulfilled, (state, action) => {
        state.addressesStatus = 'succeeded';
        state.addresses = action.payload.addresses;
      })
      .addCase(fetchAddresses.rejected, (state, action) => {
        state.addressesStatus = 'failed';
        const payload = action.payload as AccountSectionError | undefined;
        state.addressesError = payload ?? {
          message: action.error.message ?? 'Failed to load addresses',
        };
      })
      // -- createAddress -----------------------------------------------
      .addCase(createAddress.pending, (state) => {
        state.addressFormStatus = 'loading';
        state.addressFormError = null;
      })
      .addCase(createAddress.fulfilled, (state, action) => {
        state.addressFormStatus = 'succeeded';
        const created = action.payload.address;
        // If the platform marked the new record as default, mirror the
        // mutual-exclusion: clear the flag on every other address first.
        if (created.isDefaultShipping) {
          state.addresses = state.addresses.map((a) => ({
            ...a,
            isDefaultShipping: false,
          }));
        }
        state.addresses.push(created);
      })
      .addCase(createAddress.rejected, (state, action) => {
        state.addressFormStatus = 'failed';
        const payload = action.payload as AccountSectionError | undefined;
        state.addressFormError = payload ?? {
          message: action.error.message ?? 'Failed to save address',
        };
      })
      // -- updateAddress -----------------------------------------------
      .addCase(updateAddress.pending, (state) => {
        state.addressFormStatus = 'loading';
        state.addressFormError = null;
      })
      .addCase(updateAddress.fulfilled, (state, action) => {
        state.addressFormStatus = 'succeeded';
        const updated = action.payload.address;
        const index = state.addresses.findIndex((a) => a.id === updated.id);
        if (index >= 0) {
          state.addresses[index] = updated;
        }
        // Honour mutual exclusion if the update flipped the default flag.
        if (updated.isDefaultShipping) {
          state.addresses = state.addresses.map((a) =>
            a.id === updated.id ? a : { ...a, isDefaultShipping: false },
          );
        }
      })
      .addCase(updateAddress.rejected, (state, action) => {
        state.addressFormStatus = 'failed';
        const payload = action.payload as AccountSectionError | undefined;
        state.addressFormError = payload ?? {
          message: action.error.message ?? 'Failed to update address',
        };
      })
      // -- deleteAddress -----------------------------------------------
      .addCase(deleteAddress.pending, (state, action) => {
        state.addressActionStatus[action.meta.arg] = 'loading';
        state.addressActionError[action.meta.arg] = null;
      })
      .addCase(deleteAddress.fulfilled, (state, action) => {
        state.addresses = state.addresses.filter((a) => a.id !== action.payload.id);
        delete state.addressActionStatus[action.payload.id];
        delete state.addressActionError[action.payload.id];
      })
      .addCase(deleteAddress.rejected, (state, action) => {
        const id = action.meta.arg;
        state.addressActionStatus[id] = 'failed';
        const payload = action.payload as AccountSectionError | undefined;
        state.addressActionError[id] = payload ?? {
          message: action.error.message ?? 'Failed to delete address',
        };
      })
      // -- setDefaultAddress (optimistic) ------------------------------
      .addCase(setDefaultAddress.pending, (state, action) => {
        const targetId = action.meta.arg;
        state.addressActionStatus[targetId] = 'loading';
        state.addressActionError[targetId] = null;
        // Snapshot the previous default so `rejected` can roll back.
        const previousDefault = state.addresses.find((a) => a.isDefaultShipping);
        state.defaultAddressIdBeforeOptimistic = previousDefault?.id ?? null;
        // Optimistic flip — bump the target to true, siblings to false.
        state.addresses = state.addresses.map((a) => ({
          ...a,
          isDefaultShipping: a.id === targetId,
        }));
      })
      .addCase(setDefaultAddress.fulfilled, (state, action) => {
        const targetId = action.payload.address.id;
        state.addressActionStatus[targetId] = 'succeeded';
        state.defaultAddressIdBeforeOptimistic = null;
        // Reconcile with the server-canonical response (idempotent).
        state.addresses = state.addresses.map((a) =>
          a.id === targetId ? action.payload.address : { ...a, isDefaultShipping: false },
        );
      })
      .addCase(setDefaultAddress.rejected, (state, action) => {
        const targetId = action.meta.arg;
        state.addressActionStatus[targetId] = 'failed';
        const payload = action.payload as AccountSectionError | undefined;
        state.addressActionError[targetId] = payload ?? {
          message: action.error.message ?? 'Failed to set default address',
        };
        // Roll back: restore the previously-default address (or clear
        // the flag entirely if no prior default existed).
        const previousId = state.defaultAddressIdBeforeOptimistic;
        state.addresses = state.addresses.map((a) => ({
          ...a,
          isDefaultShipping: previousId !== null && a.id === previousId,
        }));
        state.defaultAddressIdBeforeOptimistic = null;
      })
      // -- setDefaultBillingAddress (server-confirmed) ------------------
      .addCase(setDefaultBillingAddress.pending, (state, action) => {
        const targetId = action.meta.arg;
        state.addressActionStatus[targetId] = 'loading';
        state.addressActionError[targetId] = null;
      })
      .addCase(setDefaultBillingAddress.fulfilled, (state, action) => {
        const targetId = action.payload.address.id;
        state.addressActionStatus[targetId] = 'succeeded';
        state.addresses = state.addresses.map((a) =>
          a.id === targetId
            ? action.payload.address
            : { ...a, isDefaultBilling: false },
        );
      })
      .addCase(setDefaultBillingAddress.rejected, (state, action) => {
        const targetId = action.meta.arg;
        state.addressActionStatus[targetId] = 'failed';
        const payload = action.payload as AccountSectionError | undefined;
        state.addressActionError[targetId] = payload ?? {
          message: action.error.message ?? 'Failed to set default billing address',
        };
      })
      // -- fetchPaymentMethods ------------------------------------------
      .addCase(fetchPaymentMethods.pending, (state) => {
        state.paymentMethodsStatus = 'loading';
        state.paymentMethodsError = null;
      })
      .addCase(fetchPaymentMethods.fulfilled, (state, action) => {
        state.paymentMethodsStatus = 'succeeded';
        state.paymentMethods = action.payload.paymentMethods;
      })
      .addCase(fetchPaymentMethods.rejected, (state, action) => {
        state.paymentMethodsStatus = 'failed';
        const payload = action.payload as AccountSectionError | undefined;
        state.paymentMethodsError = payload ?? {
          message: action.error.message ?? 'Failed to load payment methods',
        };
      })
      // -- setDefaultPaymentMethod --------------------------------------
      .addCase(setDefaultPaymentMethod.pending, (state, action) => {
        const id = action.meta.arg;
        state.paymentMethodActionStatus[id] = 'loading';
        state.paymentMethodActionError[id] = null;
      })
      .addCase(setDefaultPaymentMethod.fulfilled, (state, action) => {
        const updated = action.payload;
        state.paymentMethodActionStatus[updated.id] = 'succeeded';
        state.paymentMethods = state.paymentMethods.map((pm) => ({
          ...pm,
          isDefault: pm.id === updated.id,
        }));
      })
      .addCase(setDefaultPaymentMethod.rejected, (state, action) => {
        const id = action.meta.arg;
        state.paymentMethodActionStatus[id] = 'failed';
        const payload = action.payload as AccountSectionError | undefined;
        state.paymentMethodActionError[id] = payload ?? {
          message: action.error.message ?? 'Failed to set default card',
        };
      })
      // -- removePaymentMethod ------------------------------------------
      .addCase(removePaymentMethod.pending, (state, action) => {
        const id = action.meta.arg;
        state.paymentMethodActionStatus[id] = 'loading';
        state.paymentMethodActionError[id] = null;
      })
      .addCase(removePaymentMethod.fulfilled, (state, action) => {
        const id = action.payload;
        state.paymentMethodActionStatus[id] = 'succeeded';
        state.paymentMethods = state.paymentMethods.filter(
          (pm) => String(pm.id) !== String(id),
        );
      })
      .addCase(removePaymentMethod.rejected, (state, action) => {
        const id = action.meta.arg;
        state.paymentMethodActionStatus[id] = 'failed';
        const payload = action.payload as AccountSectionError | undefined;
        state.paymentMethodActionError[id] = payload ?? {
          message: action.error.message ?? 'Failed to remove card',
        };
      })
      // -- addPaymentMethod ---------------------------------------------
      .addCase(addPaymentMethod.pending, (state) => {
        state.addPaymentMethodStatus = 'loading';
        state.addPaymentMethodError = null;
      })
      .addCase(addPaymentMethod.fulfilled, (state) => {
        state.addPaymentMethodStatus = 'succeeded';
      })
      .addCase(addPaymentMethod.rejected, (state, action) => {
        state.addPaymentMethodStatus = 'failed';
        const payload = action.payload as AccountSectionError | undefined;
        state.addPaymentMethodError = payload ?? {
          message: action.error.message ?? 'Failed to add card',
        };
      })
      // -- fetchProfile (SF-ACCT-008) -----------------------------------
      .addCase(fetchProfile.pending, (state) => {
        state.profileStatus = 'loading';
        state.profileError = null;
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.profileStatus = 'succeeded';
        state.profile = action.payload;
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.profileStatus = 'failed';
        const payload = action.payload as AccountSectionError | undefined;
        state.profileError = payload ?? {
          message: action.error.message ?? 'Failed to load profile',
        };
      })
      // -- updateProfile ------------------------------------------------
      .addCase(updateProfile.pending, (state) => {
        state.updateProfileStatus = 'loading';
        state.updateProfileError = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.updateProfileStatus = 'succeeded';
        state.profile = action.payload;
        state.profileStatus = 'succeeded';
        state.profileError = null;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.updateProfileStatus = 'failed';
        const payload = action.payload as AccountProfileUpdateError | undefined;
        state.updateProfileError = payload ?? {
          message: action.error.message ?? 'Failed to update profile',
        };
      });
  },
});

export const {
  setAddresses,
  setPaymentMethods,
  clearAddPaymentMethodError,
  resetAccountState,
  resetAddressFormStatus,
  clearAddressActionError,
  clearArtworkDeleteError,
  prependArtworkFile,
  clearUpdateProfileError,
  clearCurrentOrder,
  clearReorderState,
} = accountSlice.actions;
export { accountSlice };
export const accountReducer = accountSlice.reducer;
export default accountReducer;

// ----------------------------------------------------------------------------
// Selectors
// ----------------------------------------------------------------------------

/**
 * Minimal root-state shape this selector module needs. Apps register
 * the `account` key dynamically via `extendStoreReducer`; using a
 * structural type here means selectors compile against `RootState`
 * even before the app boots its reducer.
 */
interface AccountSliceRoot {
  account: AccountState;
  auth: AuthState;
}

export const selectAccountState = (state: AccountSliceRoot): AccountState => state.account;

export const selectRecentOrders = (state: AccountSliceRoot): AccountOrderSummary[] => state.account.recentOrders;

// ── SF-ACCT-002 — full paginated order history selectors ────────────
export const selectOrders = (state: AccountSliceRoot): AccountOrderSummary[] =>
  state.account.orders;

export const selectOrdersMeta = (
  state: AccountSliceRoot,
): AccountPaginationMeta | null => state.account.ordersMeta;

export const selectOrdersStatus = (
  state: AccountSliceRoot,
): AccountSectionStatus => state.account.ordersStatus;

export const selectOrdersError = (
  state: AccountSliceRoot,
): AccountSectionError | null => state.account.ordersError;

export const selectArtworkCount = (state: AccountSliceRoot): number | null => state.account.artworkCount;

export const selectAddresses = (state: AccountSliceRoot): AccountAddressSummary[] => state.account.addresses;

export const selectPaymentMethods = (
  state: AccountSliceRoot,
): PaymentMethodDTO[] => state.account.paymentMethods;

/** List-fetch status for the saved payment methods. */
export const selectPaymentMethodsStatus = (
  state: AccountSliceRoot,
): AccountSectionStatus => state.account.paymentMethodsStatus;

export const selectPaymentMethodsError = (
  state: AccountSliceRoot,
): AccountSectionError | null => state.account.paymentMethodsError;

/**
 * Add Card lifecycle status — `loading` while the SetupIntent + Stripe
 * confirmation are in flight, `failed` when Stripe returns an error.
 */
export const selectAddPaymentMethodStatus = (
  state: AccountSliceRoot,
): AccountSectionStatus => state.account.addPaymentMethodStatus;

export const selectAddPaymentMethodError = (
  state: AccountSliceRoot,
): AccountSectionError | null => state.account.addPaymentMethodError;

/**
 * Per-card action lifecycle map keyed by Stripe PaymentMethod id. Card
 * rows pass their own id as the key; missing entries default to `idle`.
 */
export const selectPaymentMethodActionStatus = (
  state: AccountSliceRoot,
): Record<string, AccountSectionStatus> =>
  state.account.paymentMethodActionStatus;

export const selectPaymentMethodActionError = (
  state: AccountSliceRoot,
): Record<string, AccountSectionError | null> =>
  state.account.paymentMethodActionError;

export const selectReorderInFlightId = (state: AccountSliceRoot): number | null => state.account.reorderInFlightId;

// ── SF-ACCT-004 — reorder lifecycle selectors ─────────────────────
export const selectReorderStatus = (
  state: AccountSliceRoot,
): AccountState['reorderStatus'] => state.account.reorderStatus;

export const selectReorderUnrestorable = (
  state: AccountSliceRoot,
): AccountState['reorderUnrestorable'] => state.account.reorderUnrestorable;

export const selectReorderAddedCartItemIds = (
  state: AccountSliceRoot,
): number[] => state.account.reorderAddedCartItemIds;

export const selectReorderError = (
  state: AccountSliceRoot,
): AccountSectionError | null => state.account.reorderError;

// ── SF-ACCT-012 — cancel-reorder selectors ──────────────────────────
export const selectCancelReorderLoading = (
  state: AccountSliceRoot,
): boolean => state.account.cancelReorderLoading;

export const selectCancelReorderError = (
  state: AccountSliceRoot,
): AccountSectionError | null => state.account.cancelReorderError;

// ── SF-ACCT-003 — single-order detail selectors ─────────────────────
export const selectCurrentOrder = (
  state: AccountSliceRoot,
): AccountOrderDetail | null => state.account.currentOrder;

export const selectCurrentOrderStatus = (
  state: AccountSliceRoot,
): AccountSectionStatus => state.account.currentOrderStatus;

export const selectCurrentOrderError = (
  state: AccountSliceRoot,
): AccountSectionError | null => state.account.currentOrderError;

/**
 * Combined loading-state object — sections expose their own status so
 * containers can render skeleton/error/content per section without a
 * monolithic boolean.
 */
export interface AccountLoadingStates {
  recentOrders: AccountSectionStatus;
  artwork: AccountSectionStatus;
}

export const selectLoadingStates = createSelector(
  [
    (state: AccountSliceRoot) => state.account.recentOrdersStatus,
    (state: AccountSliceRoot) => state.account.artworkStatus,
  ],
  (recentOrders, artwork): AccountLoadingStates => ({ recentOrders, artwork }),
);

export const selectRecentOrdersError = (state: AccountSliceRoot): AccountSectionError | null =>
  state.account.recentOrdersError;

export const selectArtworkError = (state: AccountSliceRoot): AccountSectionError | null => state.account.artworkError;

// SF-ACCT-005 — full artwork-library state.
export const selectArtworkFiles = (state: AccountSliceRoot): AccountArtworkSummary[] => state.account.artworkFiles;

export const selectArtworkFilesMeta = (state: AccountSliceRoot): AccountPaginationMeta | null =>
  state.account.artworkFilesMeta;

export const selectArtworkFilesStatus = (state: AccountSliceRoot): AccountSectionStatus =>
  state.account.artworkFilesStatus;

export const selectArtworkFilesError = (state: AccountSliceRoot): AccountSectionError | null =>
  state.account.artworkFilesError;

export const selectArtworkDeleteError = (state: AccountSliceRoot): AccountArtworkDeleteError | null =>
  state.account.artworkDeleteError;

// ─── Profile selectors (SF-ACCT-008) ─────────────────────────────────
export const selectProfile = (state: AccountSliceRoot): AccountProfile | null =>
  state.account.profile;

export const selectProfileStatus = (state: AccountSliceRoot): AccountSectionStatus =>
  state.account.profileStatus;

export const selectProfileError = (state: AccountSliceRoot): AccountSectionError | null =>
  state.account.profileError;

export const selectUpdateProfileStatus = (
  state: AccountSliceRoot,
): AccountSectionStatus => state.account.updateProfileStatus;

export const selectUpdateProfileError = (
  state: AccountSliceRoot,
): AccountProfileUpdateError | null => state.account.updateProfileError;

// ─── Address-book selectors (SF-ACCT-007) ────────────────────────────
export const selectAddressesStatus = (state: AccountSliceRoot): AccountSectionStatus =>
  state.account.addressesStatus;

export const selectAddressesError = (state: AccountSliceRoot): AccountSectionError | null =>
  state.account.addressesError;

export const selectAddressFormStatus = (state: AccountSliceRoot): AccountSectionStatus =>
  state.account.addressFormStatus;

export const selectAddressFormError = (state: AccountSliceRoot): AccountSectionError | null =>
  state.account.addressFormError;

export const selectAddressActionStatus = (
  state: AccountSliceRoot,
): Record<number, AddressActionStatus> => state.account.addressActionStatus;

export const selectAddressActionError = (
  state: AccountSliceRoot,
): Record<number, AccountSectionError | null> => state.account.addressActionError;

// ----------------------------------------------------------------------------
// Account-completion selector
// ----------------------------------------------------------------------------

export type AccountCompletionItemId = 'email_verified' | 'company_name' | 'phone' | 'address' | 'payment_method';

export interface AccountCompletionItem {
  id: AccountCompletionItemId;
  label: string;
  /** Whether the checkpoint is currently satisfied. */
  complete: boolean;
  /** Route the user is sent to to satisfy this item. */
  href: string;
}

export interface AccountCompletionStatus {
  /** All checklist items, in display order. */
  items: AccountCompletionItem[];
  /** Number of `items` whose `complete` is `true`. */
  completedCount: number;
  /** `items.length` — kept for trivial percentage math in the UI. */
  totalCount: number;
  /**
   * 0–100 integer percentage of the checklist that is complete. Capped
   * at 100; rounded toward the nearest integer.
   */
  percentage: number;
}

/**
 * Pure derived selector — combines the slice's known checkpoints
 * (addresses, payment methods) with the auth slice's user record
 * (email verification, company name, phone) into a single completion
 * status. Memoized via `createSelector` so referential equality holds
 * between renders when nothing relevant changed.
 */
export const selectAccountCompletionStatus = createSelector(
  [
    (state: AccountSliceRoot) => state.auth.user,
    (state: AccountSliceRoot) => state.account.addresses,
    (state: AccountSliceRoot) => state.account.paymentMethods,
  ],
  (user, addresses, paymentMethods): AccountCompletionStatus => {
    const items: AccountCompletionItem[] = [
      {
        id: 'email_verified',
        label: 'Verify your email',
        complete: Boolean(user?.emailVerifiedAt),
        href: '/account/profile',
      },
      {
        id: 'company_name',
        label: 'Add your company name',
        complete: Boolean(user?.companyName && user.companyName.trim().length > 0),
        href: '/account/profile',
      },
      {
        id: 'phone',
        label: 'Add a phone number',
        complete: Boolean(user?.phone && user.phone.trim().length > 0),
        href: '/account/profile',
      },
      {
        id: 'address',
        label: 'Save a shipping address',
        complete: addresses.length > 0,
        href: '/account/addresses',
      },
      {
        id: 'payment_method',
        label: 'Add a payment method',
        complete: paymentMethods.length > 0,
        href: '/account/payment-methods',
      },
    ];

    const completedCount = items.filter((item) => item.complete).length;
    const totalCount = items.length;
    const percentage = totalCount === 0 ? 0 : Math.round((completedCount / totalCount) * 100);

    return { items, completedCount, totalCount, percentage };
  },
);
